<template>
    <div :class="meta?.maxWidth ? meta.maxWidth : '' + ' h-full'">
        <component
            :is="componentName"
            ref="gridRef"
            :meta="gridMeta"
            :data-source="displayData"
            :lookups-data="displayLookupData"
            :editing="editing"
            :toolbar-config="toolbarConfig"
            :toolbar-user-items="toolbarUserItems"
            :to-remove-toolbar-items-names="toRemoveToolbarItemsNames"
            :preferences-state="state.prefState"
            :locked-rows="lockedRows"
            :dotsClick="dotsClick"
            :addCustomClick="addCustomClick"
            :showGridFooter="showFooter"
            @focus-changed="handleFocusChanged"
            @save-click="onSaveClick"
            @delete-click="onDeleteClick"
            @user-data-changed="onUserDataChanged"
            @user-settings-changed="onUserSettingsChanged"
            @user-settings-reset="onUserSettingsReset"
            @refresh-click="onRefreshClick"
            @cell-click="onCellClickHandler"
            @save-settings="onSaveSettings"
            @coreMounted="onCoreMounted"
            @changesRemoved="onChangesRemoved"
            @onAddClickEvent="handleAddClickEvent"
            @toolbar-button-click="toolbarButtonClick"
        >
            <template #cellTemplate="{ data }">
                <div class="relative flex align-center w-full">
                    <div :class="'min-h-[16px] overflow-hidden w-full ' + moveNumber(data)">
                        {{ data.text }}
                    </div>
                    <div class="absolute right-0 top-[-4px]">
                        <div
                            v-if="isCellHasError(data)"
                            class="w-6 h-6 bg-red-600 text-white rounded-full text-lg flex justify-center items-center"
                            @click="event => onErrorSignClick(event, data)"
                        >
                            !
                        </div>
                    </div>
                    <div
                        v-if="showErrorMessage(data)"
                        class="absolute bg-red-500 text-white p-[5px] z-50 mt-[17px] w-full opacity-75 rounded-[7px] group-hover:opacity-100 transition duration-300 ease-in-out whitespace-pre-wrap"
                    >
                        {{ errorList[currentField] }}
                    </div>
                </div>
            </template>
            <template v-for="(_, name) in $slots" #[name]="slotProps">
                <slot :name="name" v-bind="slotProps"></slot>
            </template>
        </component>
    </div>
</template>

<script>
export default {
    name: 'ai-grid-edit',
};
</script>
<script setup>
// import aiGridEditCore from '@/components/ai-grid-edit/ai-grid-edit-core.vue';
import { computed, onUnmounted, ref } from 'vue';
import { dxUiNotify } from '@/utils/dx-helper';
import AiCustomConfirmDialog from '@/utils/ai-custom-confirm-dialog';

// STORING
import { reactive } from 'vue';
import { getDataSource } from '@/service/meta-service';
import { deleteUserDataChanges, deleteUserSettingsChanges, saveUserDataChanges } from '@/service/userdata-service';
import { api } from '@/service/http';
import { setTabReadyToSaveValue, tabState } from '@/layouts/main-layout/components/navigation-bars-data';
import { mainState } from '@/layouts/main-layout/main-layout-data';

const props = defineProps({
    meta: {
        type: Object,
        default: () => {},
    },
    action: {
        type: Function,
        default: null,
    },
    args: {
        type: Object,
        default: () => {},
    },
    lockedRows: {
        type: Array,
        default: () => [],
    },
    toolbarUserItems: {
        type: Array,
        default: () => [],
    },
    toRemoveToolbarItemsNames: {
        type: Array,
        default: () => ['choice', 'edit'],
    },
    dotsClick: {
        type: Function,
        default: metaCell => {
            console.log('ai-grid-edit default dotsClick(metaCell) metaCell: ' + JSON.stringify({}));
            return {};
        },
    },
    hasntRoute: {
        type: Boolean,
        default: false,
    },
    showFooter: {
        type: Boolean,
        default: () => true,
    },
    addCustomClick: {
        type: Boolean,
        default: false,
    },
});

const state = reactive({
    lookupDataSource: {},
    prefState: true,
    selectedRowKeys: [],
    gridChanges: [],
    userSettings: {},
    dataSource: [],
});

const emit = defineEmits([
    'onAddClickEvent',
    'editGridMounted',
    'mounted',
    'refresh',
    'deleteClick',
    'userDataChanged',
    'toolbarButtonClick',
    'userSettingsChanged',
    'changesSavedSuccessfully',
    'cellClick',
]);

const isErrorMessageVisible = ref(false);
const gridRef = ref();
const gridInstance = ref();
const systemErrorIds = ref([]);
const validationErrorsList = ref([]);
const tooltipConfigList = ref([]);
const errorList = ref([]);
const timeoutId = ref();
const currentField = ref(null);
const currentRow = ref();
const isUserDataLoaded = ref(false);
const args = computed(() => (props.args === undefined ? {} : props.args));
const displayData = computed(() => state.dataSource);
const componentName = 'ai-grid-edit-core';
const displayLookupData = computed(() => state.lookupDataSource);
const lazyFocusKeyExpr = ref();

const editing = computed(() => {
    const { rights } = props.meta;

    if (!rights) {
        return {
            enableDeleting: true,
            allowAdding: true,
            allowUpdating: true,
        };
    }

    let result = {};

    if (!rights.IDU) {
        result = {
            enableDeleting: false,
            allowAdding: false,
            allowUpdating: false,
        };
    } else {
        result = {
            enableDeleting: true,
            allowAdding: true,
            allowUpdating: true,
        };
    }

    if (Object.prototype.hasOwnProperty.call(rights, 'INS')) result.allowAdding = rights.INS;

    if (Object.prototype.hasOwnProperty.call(rights, 'UPD')) result.allowUpdating = rights.UPD;

    if (Object.prototype.hasOwnProperty.call(rights, 'DEL')) result.enableDeleting = rights.DEL;

    return result;
});

const gridMeta = computed(() => {
    const updatedMeta = { ...props.meta };
    updatedMeta.columns.forEach(col => {
        if (col.dataType !== 'boolean' && !col?.cellTemplate) col.cellTemplate = 'cellTemplate';
    });

    const columnsWithIndex = updatedMeta.columns.filter(item => item.index);
    const maxIndex = columnsWithIndex.length > 0 ? Math.max(...columnsWithIndex.map(item => item.index)) : -1;
    const isDublicate = findDuplicates(columnsWithIndex);

    if (isDublicate) throw new Error('Ошибка: индексы в колонках должны иметь уникальное значение');

    if (columnsWithIndex.length === 0) {
        updatedMeta.columns = updatedMeta.columns.map((el, index) => ({
            ...el,
            index: index,
            visible: el.visible !== undefined ? el.visible : true,
        }));
    }

    if (maxIndex !== -1) {
        updatedMeta.columns = updatedMeta.columns.map((el, index) => {
            if (el.index === undefined) {
                return {
                    ...el,
                    index: maxIndex + index + 1,
                    visible: el.visible !== undefined ? el.visible : true,
                };
            } else {
                return el;
            }
        });
    }

    return updatedMeta;
});

const setCacheOrApiDataSource = async () => {
    if (props.hasntRoute) {
        await setDataSource();
        return;
    }
    if (tabState[mainState.routePath] && tabState[mainState.routePath]?.data) {
        await setDataSource(tabState[mainState.routePath]?.data);
    } else {
        const res = await setDataSource();
        tabState[mainState.routePath] ??= {};
        tabState[mainState.routePath].data = res.data;
    }
};

const onCoreMounted = () => {
    console.log('grid edit mounted');
    gridInstance.value = gridRef.value.gridInstance;
    gridInstance.value.on('contentReady', onContentReady);
    // emit('mounted');

    isUserDataLoaded.value = false;
    setTabReadyToSaveValue(false);

    // await fetchAndSetUserSettings({ viewId: props.meta.viewId });

    const gridState = gridInstance.value.state();
    const updatedGridState = { ...gridState, ...state.userSettings, selectedRowKeys: state.selectedRowKeys };

    gridInstance.value.state(updatedGridState);
    setUserDataChanges(state.gridChanges);

    isUserDataLoaded.value = true;

    if (props.action)
        props.action({
            instance: gridInstance.value,
            meta: props.meta,
            refresh: onRefreshClick,
            save: _onSaveClick,
            componentRef: gridRef.value,
            setUserDataChanges,
            setDataSource,
            setFocusdRow,
        });

    emit('editGridMounted');
};

const handleAddClickEvent = e => {
    emit('onAddClickEvent', e);
};

const setFocusdRow = keyExprValue => {
    if (displayData.value.length) {
        _setFocusedRow(keyExprValue);
    } else {
        lazyFocusKeyExpr.value = keyExprValue;
    }
};

const _setFocusedRow = keyExprValue => {
    const neededRowIndex = gridInstance.value.getRowIndexByKey(keyExprValue);
    gridInstance.value.option('focusedRowIndex', neededRowIndex);
    gridInstance.value.option('focusedRowKey', keyExprValue);
};

const onChangesRemoved = () => {
    emit('changesRemoved');
};

const setUserDataChanges = (userData, isReady) => {
    gridInstance.value.option('editing.changes', userData);
    state.gridChanges = userData;

    if (isReady) {
        setTabReadyToSaveValue(true);
    }
};

const onSaveSettings = () => {
    emit('save-settings');
};

const showErrorMessage = data => {
    return (
        isCellHasError(data) &&
        isErrorMessageVisible.value &&
        data.column.dataField === currentField.value &&
        data.row.key === currentRow.value
    );
};

const findDuplicates = columns => {
    const indexes = columns.length > 0 ? columns.map(el => el.index) : [];
    const uniqueIndexes = new Set();
    for (let i of indexes) {
        if (uniqueIndexes.has(i)) return true;
        uniqueIndexes.add(i);
    }

    return false;
};

const onErrorSignClick = (event, data) => {
    if (timeoutId.value) {
        clearTimeout(timeoutId.value);
    }
    event.stopPropagation();
    errorList.value = findMessage(data);
    currentField.value = data.column.dataField;
    currentRow.value = data.row.key;
    isErrorMessageVisible.value = true;
    timeoutId.value = setTimeout(() => {
        isErrorMessageVisible.value = false;
    }, 15000);
};

const toolbarButtonClick = e => {
    isErrorMessageVisible.value = false;
    emit('toolbarButtonClick', e);
};

const getLookupColumns = () => {
    const columns = [];
    for (const col of props.meta.columns) {
        if (col.editor) {
            const isLookupEditor =
                col.editor.type === 'ai-drop-down' ||
                col.editor.type === 'ai-popup' ||
                col.editor.type === 'ai-popup-multiselect';
            if (isLookupEditor) {
                columns.push(col);
            }
        }
    }
    return columns;
};
const generateLookupDs = (col, ds) => {
    const lookupDs = [];
    for (const row of ds) {
        const lookupRow = {};
        lookupRow[col.dataField] = row[col.dataField];
        lookupRow[col.editor.text] = row[col.editor.text];
        lookupDs.push(lookupRow);
    }
    lookupDs.push({ id_ro_grid_11_popup: 9, name_ro_11: 'hi' });
    return lookupDs;
};

const getLookUpsDs = dataSource => {
    const columns = getLookupColumns();
    const lookups = {};
    for (const col of columns) {
        const lookupDs = generateLookupDs(col, dataSource);
        lookups[col.dataField] = lookupDs;
    }

    return lookups;
};

const setDataSource = async (cacheData, selectArgs) => {
    const { requiredArgs } = props.meta;

    if (requiredArgs) {
        const hasAllRequiredArgs = requiredArgs.select.every(arg =>
            Object.prototype.hasOwnProperty.call(args.value.select, arg)
        );

        if (!hasAllRequiredArgs) {
            state.lookupDataSource = [];
            state.dataSource = [];
            return;
        }
    }

    let result;

    if (cacheData) {
        result = { result: true, data: cacheData };
    } else {
        const finalArgs = selectArgs ? selectArgs : args.value?.select;
        result = await getDataSource(props.meta.api.select, finalArgs);
    }
    // const { result, data } = await getDataSource(props.meta.api.select, args.value.select);

    state.lookupDataSource = getLookUpsDs(result.result && result.data ? result.data : []);
    state.dataSource = result.result && result.data ? result.data : [];
    return result;
};

// const fetchAndSetUserData = async args => {
//     let userDataChanges;
//     let res;
//     if (
//         Object.keys(tabState).length &&
//         tabState[window.location.pathname] &&
//         tabState[window.location.pathname].changes
//     ) {
//         userDataChanges = tabState[window.location.pathname].changes;
//         res = true;
//     } else {
//         const { result, changes } = await getUserDataChanges(args);
//         userDataChanges = changes;
//         res = result;

//         tabState[window.location.pathname] ??= {};
//         tabState[window.location.pathname].changes = changes;
//     }

//     if (res && userDataChanges) {
//         const { selectedRowKeys, gridChanges, lookupData } = userDataChanges;
//         state.selectedRowKeys = selectedRowKeys ? selectedRowKeys : [];
//         state.gridChanges = gridChanges ? gridChanges : [];

//         if (lookupData) {
//             Object.keys(state.lookupDataSource).forEach(key => {
//                 state.lookupDataSource[key] = [...state.lookupDataSource[key], ...lookupData];
//             });
//         }
//     } else {
//         state.selectedRowKeys = [];
//         state.gridChanges = [];
//     }
// };

// const fetchAndSetUserSettings = async args => {
//     let userDataSettings;
//     let res;
//     if (
//         Object.keys(tabState).length &&
//         tabState[window.location.pathname] &&
//         tabState[window.location.pathname].settings
//     ) {
//         userDataSettings = tabState[window.location.pathname].settings;
//         res = true;
//     } else {
//         const { result, settings } = await getUserSettingsChanges(args);
//         userDataSettings = settings;
//         res = result;

//         tabState[window.location.pathname] ??= {};
//         tabState[window.location.pathname].settings = settings;
//     }

//     // const { result, settings } = await getUserSettingsChanges(args);

//     if (res && userDataSettings) {
//         state.userSettings = userDataSettings;
//         setPrefState(userDataSettings);
//     } else {
//         state.userSettings = {};
//         state.prefState = true;
//     }
// };

const setPrefState = settings => {
    if (!settings) {
        state.prefState = true;
        return;
    }
    switch (settings.preferencesState) {
        case false:
            state.prefState = false;
            break;
        case undefined:
        case null:
        case true:
        default:
            state.prefState = true;
            break;
    }
};
// STORING

const isCellHasError = ({ column, row }) => {
    let result = false;
    validationErrorsList.value.forEach(({ item: { greyEminence }, field }) => {
        if (greyEminence === row.key && field === column.dataField) result = true;
    });
    return result;
};

const moveNumber = cellData => {
    const result = isCellHasError(cellData);
    if (result) {
        return 'pr-[30px]';
    }
    return '';
};

const findMessage = data => {
    const errorMessagesMap = {};

    tooltipConfigList.value.forEach(item => {
        const errors = tooltipConfigList.value.filter(error => error.key === data.key && error.field === item.field);
        if (errors.length > 0) {
            errorMessagesMap[item.field] = errors.map(err => err.message).join(' ');
        } else {
            errorMessagesMap[item.field] = '';
        }
    });

    return errorMessagesMap;
};

const onCellClickHandler = (viewId, key) => {
    emit('cellClick', viewId, key);
    isErrorMessageVisible.value = false;
};

const onContentReady = event => {
    highlightErrors();

    systemErrorIds.value.forEach(id => {
        const index = gridInstance.value.getRowIndexByKey(id);
        if (index > -1) {
            const element = gridInstance.value.getRowElement(index)[0];
            element.classList.add('bg-red-500');
        }
    });
};

const highlightErrors = () => {
    validationErrorsList.value.forEach(error => {
        const rowIndex = gridInstance.value.getRowIndexByKey(error.item.greyEminence);
        const cell = gridInstance.value.getCellElement(rowIndex, error.field);

        if (cell) {
            cell.classList.add('dx-validator');
            cell.classList.add('dx-datagrid-invalid');
            cell.classList.add('dx-datagrid-validator');
            cell.classList.add('group');
        }
    });
};
const onRefreshClick = async (gridChanges, processedData) => {
    if (gridChanges.length > 0 && !props.meta.cancelRefreshConfirmDialog) {
        const hasValidChanges = gridChanges.some(update => {
            if (update.type === 'remove') return true;

            return !(
                update?.data &&
                typeof update.data === 'object' &&
                Object.keys(update.data).every(value => value === 'undefined')
            );
        });

        if (hasValidChanges) {
            const result = await AiCustomConfirmDialog('Сохранить изменения?', 'Запрос на сохранение');

            if (result === 1) {
                await onSaveClick(processedData);
                gridInstance.value.clearSelection();
                emit('refresh');
                return;
            }
            if (result === -1) return;
        }
    }

    isUserDataLoaded.value = false;
    setUserDataChanges([]);
    gridInstance.value.clearSelection();
    isUserDataLoaded.value = true;

    systemErrorIds.value = [];
    validationErrorsList.value = [];

    await setDataSource();

    dxUiNotify('Данные обновлены', 'success');
    emit('refresh');
};

const handleFocusChanged = async (row, viewId, rowIndex, component) => {
    emit('gridFocusChanged', row, viewId, rowIndex, component);
};

const onSaveClick = async userChangesPromise => {
    if (typeof userChangesPromise.then === 'undefined') {
        const userChanges = userChangesPromise;
        await _onSaveClick(userChanges);
    } else {
        userChangesPromise.then(async userChanges => {
            await _onSaveClick(userChanges);
        });
    }
};
const _onSaveClick = async userChanges => {
    systemErrorIds.value = [];
    validationErrorsList.value = [];
    tooltipConfigList.value = [];

    const { errors, validationErrors } = await commitDataChanges(props.meta.api.save, userChanges, args.value.save);

    const res = await processErrors(errors ?? [], validationErrors ? validationErrors : []);
    return res;
};

const onDeleteClick = async rowsToDelete => {
    if (!props.meta.dontShowDeleteConfirmDialog) {
        const result = await AiCustomConfirmDialog(
            'Строки будут безвозвратно удалены. Продолжить?',
            'Запрос на удаление'
        );

        if (result === -1 || result === 0) {
            gridInstance.value.clearSelection();
            return;
        }
    }

    gridInstance.value.clearSelection();

    if (props.meta?.api?.save) {
        const { errors, validationErrors } = await commitDataChanges(
            props.meta.api.save,
            rowsToDelete,
            args.value.save
        );
        await processErrors(errors ?? [], validationErrors ? validationErrors : [], true);
    } else {
        emit('deleteClick', rowsToDelete);
    }
};

const commitDataChanges = async (url, changes, args) => {
    const gridChanges = gridInstance.value.option('editing.changes');
    const apiArgs = { ...changes, ...args, ...gridChanges };
    try {
        const result = await api(url, { ...apiArgs });
        return result;
    } catch (error) {
        return {
            result: false,
            error: error.message,
            uuid: 'f0db6121-001b-4753-9403-f9a87231a5df',
        };
    }
};
const processErrors = async (errors, validationErrors, deletingCase, reorderedRows) => {
    if (errors.length === 0 && validationErrors.length === 0) {
        if (!deletingCase) {
            setUserDataChanges([]);
            // gridInstance.value.option('editing.changes', []);
        }

        await setDataSource();
        dxUiNotify('Изменения данных прошли успешно', 'success');
        gridInstance.value.clearSelection();
        saveUserDataChanges;
        await deleteUserDataChanges({ viewId: props.meta.viewId });
        emit('changesSavedSuccessfully');
        return true;
    }

    if (validationErrors.length > 0) {
        const validationErrorIds = validationErrors.map(err => err.item.greyEminence);
        validationErrorsList.value = [...validationErrors];

        validationErrorsList.value.forEach(error => {
            tooltipConfigList.value.push({
                key: error.item.greyEminence,
                field: error.field,
                message: error.message,
            });
        });

        if (!deletingCase) {
            const filteredChanges = gridInstance.value
                .option('editing.changes')
                .filter(change => validationErrorIds.includes(change.key));

            // gridInstance.value.option('editing.changes', filteredChanges);
            setUserDataChanges(filteredChanges);
        }

        await setDataSource();

        dxUiNotify('Ошибка сохранения данных', 'error');
        setTimeout(() => highlightErrors(), 500);
        return false;
    }

    if (errors?.length > 0) {
        const errorMsgs = [];
        errorMsgs.push(errors.message);
        if (!deletingCase) {
            const filteredChanges = gridInstance.value
                .option('editing.changes')
                .filter(change => systemErrorIds.value.includes(change.key));

            // gridInstance.value.option('editing.changes', filteredChanges);
            setUserDataChanges(filteredChanges);
        }

        await setDataSource();
        setFocus(-1);

        dxUiNotify(`Ошибка базы данных: ${errors[0].message}`, 'error');
        return false;
    }
};

const onUserDataChanged = async userData => {
    if (!isUserDataLoaded.value) return;
    const args = {
        viewId: props.meta.viewId,
        instanceId: null,
        changes: userData,
    };

    // if (props.action) {
    //     props.action({
    //         gridChanges: gridInstance.value.option('editing.changes'),
    //         instance: gridInstance.value,
    //         meta: props.meta,
    //         refresh: onRefreshClick,
    //         save: _onSaveClick,
    //         componentRef: gridRef.value,
    //     });
    // }
    emit('userDataChanged', args);
    // await setUserDataChanges(args);
};

const onUserSettingsChanged = async (userSettingsData, autoSaveMode) => {
    if (!isUserDataLoaded.value) return;

    const args = {
        viewId: props.meta.viewId,
        settings: userSettingsData,
    };

    emit('userSettingsChanged', args, autoSaveMode);
    state.userSettings = userSettingsData;
    setPrefState(userSettingsData);
};

const onUserSettingsReset = async () => {
    gridInstance.value.state({});
    state.userSettings = {};
    state.prefState = true;
    await deleteUserSettingsChanges({ viewId: props.meta.viewId, settings: {} });
};

const setFocus = index => {
    setTimeout(() => {
        if (index < 0) {
            gridInstance.value.option('focusedColumnIndex', -1);
            gridInstance.value.option('focusedRowIndex', -1);
            gridInstance.value.option('focusedRowKey', null);
            return;
        }
        const key = gridInstance.value.getKeyByRowIndex(index);

        gridInstance.value.option('focusedColumnIndex', 0);
        gridInstance.value.option('focusedRowIndex', index);
        gridInstance.value.option('focusedRowKey', key);
    }, 300);
};

const toolbarConfig = {
    order: [
        {
            name: 'refreshButton',
        },
        {
            name: 'copyButton',
        },
        {
            name: 'addButton',
        },
        {
            name: 'deleteButton',
        },
        {
            name: 'saveChangesButton',
        },
        {
            name: 'revertButton',
        },
    ],
};

const checkBeforeClose = async () => {
    const processedData = await gridRef.value?.prepareChangesForSaving();
    const userData = gridRef.value?.userData;
    if (userData?.gridChanges.length > 0) {
        const result = await AiCustomConfirmDialog('Сохранить изменения?', 'Запрос на сохранение');

        if (result === 1) {
            await onSaveClick(processedData);
            return validationErrorsList.value.length === 0;
        }
        if (result === -1) return false;
    }

    isUserDataLoaded.value = false;
    setUserDataChanges([]);
    // gridInstance.value.option('editing.changes', []);
    gridInstance.value.clearSelection();
    isUserDataLoaded.value = true;

    systemErrorIds.value = [];
    validationErrorsList.value = [];
    return true;
};

// Добавляет в DS cellTemplate значение, заданное программно (для того, чтобы отображалось в гриде)
// fieldExpr: нэйм колонки в которой cellTemplate
// valueExpr: нэйм колонки в которой лежат ключи
// value: новый ключ
// displayExpr: нэйм колонки в которой лежит текст
// displayValue: новый текст
const addCellTemplateValue = (fieldExpr, valueExpr, value, displayExpr, displayValue) => {
    if (gridRef.value.lookupAddData) {
        // Если lookupAddData еще не содержит ключ со столбцом, инициализируем его как пустой массив
        if (!gridRef.value.lookupAddData[fieldExpr]) {
            gridRef.value.lookupAddData[fieldExpr] = [];
        }
        // Проверяем, существует ли уже элемент с таким же value
        const exists = gridRef.value.lookupAddData[fieldExpr].some(item => item[valueExpr] === value);
        // Если такого элемента нет, добавляем новый
        if (!exists) {
            gridRef.value.lookupAddData[fieldExpr] = [
                ...(gridRef.value.lookupAddData[fieldExpr] || []), // сохраняем старые данные
                { [valueExpr]: value, [displayExpr]: displayValue }, // добавляем новый элемент
            ];
        }
    }
};

onUnmounted(() => {
    setTabReadyToSaveValue(false);
});

defineExpose({
    setDataSource,
    gridInstance,
    getGridDataSource: ms => gridRef.value.getGridDataSource(ms),
    checkBeforeClose,
    gridRef: gridRef,
    setUserDataChanges,
    save: _onSaveClick,
    setFocusdRow,
    setCacheOrApiDataSource,
    addCellTemplateValue,
    prepareChangesForSaving: gridRef.value?.prepareChangesForSaving,
    prepareChangesForSavingFunc: () => gridRef.value?.prepareChangesForSaving(),
    onRefreshClick,
    getButtonsRefs: () => gridRef.value?.getButtonsRefs(),
    addClick: () => gridRef.value.onAddClick(),
});
</script>
<style scoped>
:deep(.dx-datagrid-rowsview .dx-row > td) {
    overflow: visible;
}

:deep(.dx-editor-cell) {
    overflow: hidden !important;
}
</style>
